import type { StrategyRdCatalog } from "../types/strategyRd";
import {
  strategyRdEntryModeLabel,
  strategyRdKnownConceptLabel,
} from "./strategyRdConceptCopy";
import {
  strategyRdConceptHiddenFromPicker,
  strategyRdFlattenLegacyIds,
} from "./strategyRdConcepts";

const WIRE_PHRASES: Array<[RegExp, string]> = [
  [/polarity:\s*"bull"/gi, "`Bullish`"],
  [/polarity:\s*"bear"/gi, "`Bearish`"],
  [/polarity:\s*"both"/gi, "`Both directions`"],
  [/side:\s*"both"/gi, "`Both`"],
  [/side:\s*"long"/gi, "Long"],
  [/side:\s*"short"/gi, "Short"],
];

/** Official names for ids TES may echo (picker row, or its legacy concrete id). */
export function strategyRdCoachConceptLabels(
  catalog: StrategyRdCatalog | null | undefined
): Map<string, string> {
  const labels = new Map<string, string>();
  const concepts = catalog?.concepts || [];
  for (const concept of concepts) {
    if (strategyRdConceptHiddenFromPicker(concept)) continue;
    const name = (concept.name || "").trim();
    if (!concept.id || !name) continue;
    labels.set(concept.id, name);
    for (const legacyId of strategyRdFlattenLegacyIds(concept.legacy_ids)) {
      labels.set(legacyId, name);
    }
  }
  for (const concept of concepts) {
    if (!concept.id || labels.has(concept.id)) continue;
    const parent = String(concept.superseded_by || "").trim();
    const parentName = parent ? labels.get(parent) : "";
    const own = (concept.name || "").trim();
    if (parentName) labels.set(concept.id, parentName);
    else if (own) labels.set(concept.id, own);
  }
  return labels;
}

const ENTRY_MODE_IDS = [
  "signal_close",
  "signal_open",
  "next_open",
  "break_close",
  "retest_then_close",
  "rejection",
  "limit_at_level",
];

const WIRE_VALUE_LABELS: Record<string, string> = {
  both: "Both",
  long: "Long",
  short: "Short",
  bull: "Bullish",
  bear: "Bearish",
  high: "High",
  low: "Low",
};

function labelForId(id: string, labels: Map<string, string>): string | null {
  return (
    labels.get(id) ||
    strategyRdKnownConceptLabel(id) ||
    (ENTRY_MODE_IDS.indexOf(id) >= 0 ? strategyRdEntryModeLabel(id) : null) ||
    WIRE_VALUE_LABELS[id] ||
    null
  );
}

function markSetting(name: string): string {
  return `\`${name}\``;
}

function markComponent(name: string): string {
  return `**${name}**`;
}

/** Lab controls. Bold. Their chosen values stay blue. */
const COMPONENT_PHRASES = [
  "entry settings",
  "entry level",
  "entry mode",
  "entry side",
  "trade side",
  "candle type",
  "confluences",
  "confluence",
  "polarity",
  "structure",
  "sessions",
  "session",
  "timeframe",
  "setup",
  "entry",
  "longs",
  "shorts",
  "long",
  "short",
  "side",
];

const SETTING_PHRASES = [
  "Both directions",
  "Bullish FVG",
  "Bearish FVG",
  "Bullish fair value gap",
  "Bearish fair value gap",
  "Prior liquidity sweep",
  "Liquidity sweep",
  "Bullish",
  "Bearish",
  "Both",
  "High",
  "Low",
  ...ENTRY_MODE_IDS.map((id) => strategyRdEntryModeLabel(id)),
];

const COMPONENT_PHRASE_KEYS = new Set(COMPONENT_PHRASES.map((phrase) => phrase.toLowerCase()));

function insideMark(before: string): boolean {
  const boldOpen = (before.match(/\*\*/g) || []).length % 2 === 1;
  const codeOpen = (before.match(/`/g) || []).length % 2 === 1;
  return boldOpen || codeOpen;
}

function emphasizePhrases(
  text: string,
  phrases: string[],
  wrap: (name: string) => string,
  display: (matched: string) => string
): string {
  const ordered = phrases
    .map((phrase) => phrase.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  let out = text;
  for (const phrase of ordered) {
    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(?<![A-Za-z0-9_-])${escaped}(?![A-Za-z0-9_-])`, "gi");
    out = out.replace(re, (full, offset: number) => {
      if (insideMark(out.slice(0, offset))) return full;
      return wrap(display(phrase));
    });
  }
  return out;
}

function titleComponent(phrase: string): string {
  if (!phrase) return phrase;
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/** Catalog concepts are values (Bullish FVG, Prior liquidity sweep), same as entry settings. */
function conceptSettingNames(
  catalog: StrategyRdCatalog | null | undefined,
  labels: Map<string, string>
): string[] {
  const names = new Set<string>(SETTING_PHRASES);
  for (const name of Array.from(labels.values())) {
    const trimmed = name.trim();
    if (trimmed) names.add(trimmed);
  }
  for (const concept of catalog?.concepts || []) {
    const own = (concept.name || "").trim();
    if (own) names.add(own);
    const known = concept.id ? strategyRdKnownConceptLabel(concept.id) : null;
    if (known) names.add(known);
  }
  return Array.from(names).filter((name) => {
    if (name.length < 4) return false;
    return !COMPONENT_PHRASE_KEYS.has(name.toLowerCase());
  });
}

function findNameRanges(
  text: string,
  names: string[]
): Array<{ start: number; end: number; name: string }> {
  const ranges: Array<{ start: number; end: number; name: string }> = [];
  const lower = text.toLowerCase();
  let cursor = 0;
  while (cursor < text.length) {
    let best: { start: number; end: number; name: string } | null = null;
    for (const name of names) {
      const at = lower.indexOf(name.toLowerCase(), cursor);
      if (at < 0) continue;
      const before = at === 0 || !/[a-z0-9_]/i.test(text.charAt(at - 1));
      const end = at + name.length;
      const after = end >= text.length || !/[a-z0-9_]/i.test(text.charAt(end));
      if (!before || !after) continue;
      if (!best || at < best.start || (at === best.start && name.length > best.name.length)) {
        best = { start: at, end, name };
      }
    }
    if (!best) break;
    ranges.push(best);
    cursor = best.end;
  }
  return ranges;
}

/** Pull concept names out of a bold label so they render blue, and leave the control bold. */
function liftConceptNamesOutOfBold(text: string, names: string[]): string {
  const ordered = names.slice().sort((a, b) => b.length - a.length);
  return text.replace(/\*\*([^*]+)\*\*/g, (full, inner: string) => {
    if (inner.length > 80 || /[.!?]/.test(inner)) return full;
    const ranges = findNameRanges(inner, ordered);
    if (ranges.length === 0) return full;
    let cursor = 0;
    let rebuilt = "";
    for (const range of ranges) {
      const before = inner.slice(cursor, range.start);
      if (before.trim()) rebuilt += `${markComponent(before.trim())} `;
      else rebuilt += before;
      rebuilt += markSetting(range.name);
      cursor = range.end;
    }
    const after = inner.slice(cursor);
    if (after.trim()) rebuilt += ` ${markComponent(after.trim())}`;
    else rebuilt += after;
    return rebuilt;
  });
}

/** Your own chat line: the words you sent, with Coach / why-no-hits wording only. */
export function strategyRdCoachUserTextForDisplay(text: string): string {
  if (!text) return text;
  return text
    .replace(/\bCoach\b/g, "TES")
    .replace(/\bwhy no hits\b/gi, (phrase) =>
      phrase.startsWith("W") ? "Why no matches" : "why no matches"
    );
}

/**
 * TES replies are written with catalog ids. Show the trader the official name.
 */
export function strategyRdCoachReplyForDisplay(
  markdown: string,
  catalog?: StrategyRdCatalog | null
): string {
  if (!markdown) return markdown;
  const labels = strategyRdCoachConceptLabels(catalog);
  let text = markdown
    .replace(/\bCoach\b/g, "TES")
    .replace(/\bwhy no hits\b/gi, (phrase) =>
      phrase.startsWith("W") ? "Why no matches" : "why no matches"
    );
  for (const [pattern, replacement] of WIRE_PHRASES) {
    text = text.replace(pattern, replacement);
  }
  text = text.replace(/\s*\(([a-z][a-z0-9_]*)\)/g, (full, id: string, offset: number, whole: string) => {
    const name = labelForId(id, labels);
    if (!name) return full;
    const before = whole.slice(0, offset).replace(/[`*\s]+$/g, "");
    const tail = before.slice(-name.length);
    if (tail.toLowerCase() === name.toLowerCase()) return "";
    return ` ${markSetting(name)}`;
  });
  text = text.replace(
    /\s*\(([^)\n]{1,80})\)/g,
    (full, inner: string, offset: number, whole: string) => {
      const gloss = String(inner)
        .trim()
        .replace(/^[`*]+|[`*]+$/g, "")
        .replace(/\s+/g, " ");
      if (!gloss || /^[a-z][a-z0-9_]*$/.test(gloss)) return full;
      const before = whole.slice(0, offset).replace(/[`*\s]+$/g, "");
      const tail = before.slice(-gloss.length);
      if (tail.toLowerCase() === gloss.toLowerCase()) return "";
      return full;
    }
  );
  text = text.replace(/side as [`']?(both|long|short)[`']?/gi, (_, side: string) => {
    const name = WIRE_VALUE_LABELS[side.toLowerCase()] || side;
    const value = name === "Long" || name === "Short" ? name : markSetting(name);
    return `${markComponent("Trade side")} as ${value}`;
  });
  const ids = new Set<string>(Array.from(labels.keys()).concat(ENTRY_MODE_IDS));
  const known = markdown.match(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g) || [];
  for (const id of known) {
    if (labelForId(id, labels)) ids.add(id);
  }
  const ordered = Array.from(ids).sort((a, b) => b.length - a.length);
  for (const id of ordered) {
    const name = labelForId(id, labels);
    if (!name || name === id) continue;
    const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(
      "(^|[^A-Za-z0-9_`])`?" + escaped + "`?(?=$|[^A-Za-z0-9_`])",
      "g"
    );
    text = text.replace(re, (_full, prefix: string) => `${prefix}${markSetting(name)}`);
  }
  const settingNames = conceptSettingNames(catalog, labels);
  text = liftConceptNamesOutOfBold(text, settingNames);
  text = emphasizePhrases(text, settingNames, markSetting, (phrase) => phrase);
  text = emphasizePhrases(text, COMPONENT_PHRASES, markComponent, titleComponent);
  return text.replace(/[ \t]{2,}/g, " ").replace(/ +([.,;:])/g, "$1");
}
